export type ReportStatus = 'live' | 'coming_soon';
export type ReportGroup = 'alumni' | 'system';
export type AbilityMarkerCategory = 'workProfile' | 'demographics' | 'positionStrength';

export type ReportDef = {
    id: string;
    label: string;
    group: ReportGroup;
    icon: string;
    status: ReportStatus;
    description: string;
};

export const REPORTS: ReportDef[] = [
    {
        id: 'events',
        label: 'Event Participation',
        group: 'alumni',
        icon: 'bi-calendar-event',
        status: 'live',
        description: 'All events on-campus, off-campus and online — including alumni who visited as recruiters, panelists, guests or attendees.',
    },
    // {
    //     id: 'placements',
    //     label: 'Placements',
    //     group: 'alumni',
    //     icon: 'bi-briefcase',
    //     count: '2,146',
    //     status: 'live',
    //     description: 'Placement records with company classification, salary quadrants, batch percentile and placement focus flag.',
    // },
    {
        id: 'rewards',
        label: 'Rewards & Recognitions',
        group: 'alumni',
        icon: 'bi-award',
        status: 'live',
        description: 'Awards conferred by the institute and by external/national/international bodies.',
    },
    // {
    //     id: 'guidance',
    //     label: 'Guidance Champions',
    //     group: 'alumni',
    //     icon: 'bi-mortarboard',
    //     count: '418',
    //     status: 'live',
    //     description: 'Alumni who have delivered webinars, guest lectures, mentorship sessions or other student-facing guidance.',
    // },
    // {
    //     id: 'stories',
    //     label: 'Success Stories',
    //     group: 'alumni',
    //     icon: 'bi-star',
    //     count: '196',
    //     status: 'live',
    //     description: 'Alumni with high marketability quotient or notable narratives, segmented into Super Achiever, Achiever, Hustler and Late Bloomer.',
    // },
    {
        id: 'markers',
        label: 'Ability Markers',
        group: 'alumni',
        icon: 'bi-people',
        status: 'live',
        description:
            'Upload alumni (uuid / registered_email_id / institute_id + custom fields). Ability markers are read from the system.',
    },
    // {
    //     id: 'uploadlog',
    //     label: 'Upload Log',
    //     group: 'system',
    //     icon: 'bi-cloud-arrow-up',
    //     count: undefined,
    //     status: 'live',
    //     description: 'History of data uploads across all reports — rows added, updated, skipped, error links.',
    // },
];

export const DEFAULT_REPORT_ID = 'events';

/** Ability tab field catalog. Codes match profile_markers.marker_field. */
export type AbilityMarkerField = {
    code: string;
    label: string;
    category: AbilityMarkerCategory;
    popular?: boolean;
};

/** Chips on the Ability Markers strip before any are added from Filters. */
export const DEFAULT_MARKER_CHIPS = [
    'is_leadership_role_in_500_company',
    'work_experience',
    'is_growing_startup_founder',
    'mediaMentions',
    'genderCode',
    'isSpouseSameInstitute',
];

export const ABILITY_MARKER_FIELDS: AbilityMarkerField[] = [
    { code: 'is_leadership_role_in_large_company', label: 'Leadership Role in Large Company', category: 'workProfile' },
    { code: 'is_leadership_role_in_500_company', label: 'Leadership Role in Fortune 500 Company', category: 'workProfile', popular: true },
    { code: 'work_experience', label: 'Work Experience > 15 Years', category: 'workProfile', popular: true },
    { code: 'is_leadership_role_in_a_listed_company', label: 'Leadership Role in a Listed Company', category: 'workProfile' },
    { code: 'is_leadership_role_in_sme_company', label: 'Leadership Role in SME Company', category: 'workProfile' },
    { code: 'is_growing_startup_founder', label: 'Growing Start-up Founder', category: 'workProfile', popular: true },
    { code: 'is_mid_senior_role_in_fortune_company', label: 'Mid-Senior Role in Fortune 500 Company', category: 'workProfile' },
    { code: 'director_or_board_member', label: 'Director OR Board Member of Company', category: 'workProfile' },
    { code: 'runs_a_business', label: 'Runs a Business', category: 'workProfile' },
    { code: 'seniority_level', label: 'Seniority Level', category: 'workProfile' },
    { code: 'working_in_education_company', label: 'Working in Education Company', category: 'workProfile' },
    { code: 'age', label: 'Age', category: 'demographics' },
    { code: 'genderCode', label: 'Gender', category: 'demographics', popular: true },
    { code: 'isSpouseSameInstitute', label: 'Spouse also from same Institute', category: 'demographics', popular: true },
    { code: 'isSiblingSameInstitute', label: 'Sibling also from same Institute', category: 'demographics', popular: true },
    { code: 'StudiedAbroad', label: 'Study Abroad', category: 'demographics', popular: true },
    { code: 'CurrentlyAbroad', label: 'Currently Abroad', category: 'demographics', popular: true },
    { code: 'LivedAbroadandBack', label: 'Lived Abroad and Back', category: 'demographics', popular: true },
    { code: 'DidnotTakeEducationloan', label: 'Took Education Loan', category: 'demographics' },
    { code: 'iswealthyNeighborhood', label: 'Wealthy Neighborhood', category: 'demographics' },
    { code: 'celebrity', label: 'Celebrity', category: 'positionStrength' },
    { code: 'wealthyfamily', label: 'Wealthy Family', category: 'positionStrength' },
    { code: 'politicalConnection', label: 'Political Connection', category: 'positionStrength' },
    { code: 'influentialPositionOfPower', label: 'Influential Position of Power', category: 'positionStrength' },
    { code: 'ownsLuxuryCar', label: 'Owns Luxury Car', category: 'positionStrength' },
    { code: 'investedinaCompany', label: 'Invested in a Company', category: 'positionStrength' },
    { code: 'featuredinHighFortuneRichestList', label: 'Featured in Fortune Richest List', category: 'positionStrength' },
    { code: 'featuredinHighTaxPayerList', label: 'Featured in High Tax Payer List', category: 'positionStrength' },
    { code: 'mediaMentions', label: 'Media Mentions', category: 'positionStrength', popular: true },
    { code: 'donatedAnyCauseInPast', label: 'Donated to any cause in Past', category: 'positionStrength' },
    { code: 'donatedToOtherInstitutes', label: 'Donated to other Institutes', category: 'positionStrength' },
    { code: 'donatedInPast', label: 'Donated in Past', category: 'positionStrength' },
    { code: 'recruitedInPast', label: 'Recruited in Past', category: 'positionStrength' },
    { code: 'recruitingFromOtherInstitutes', label: 'Recruiting from other Institutes', category: 'positionStrength' },
];

export const STANDARD_FILTERS = ['Campus', 'Program', 'Batch Year', 'Profile Status', 'Alma Mater Pride', 'Marketability Quotient', 'Vibrancy'];

export const DEFAULT_PILL_FILTERS = ['Campus', 'Program', 'Batch Year', 'Profile Status'];

export const DEFAULT_COLUMNS = ['Alumni', 'Program / Batch', 'Current Company', 'Designation', 'Location', 'Markers'];

export const AVAILABLE_COLUMNS = ['Total markers', 'Alma Mater Pride', 'Marketability Quotient', 'Vibrancy', 'Profile Status'];

export type MockMarkerRow = {
    name: string;
    batch: string;
    company: string;
    designation: string;
    location: string;
    markers: string[];
};

export const MOCK_MARKER_ROWS: MockMarkerRow[] = [
    { name: 'Rahul Menon', batch: 'MBA · 2015', company: 'Goldman Sachs', designation: 'VP — Investment Banking', location: 'London, UK', markers: ['F500 Leadership', '15+ yrs exp', 'Currently Abroad'] },
    { name: 'Sneha Kulkarni', batch: 'MBA · 2019', company: 'McKinsey & Co', designation: 'Engagement Manager', location: 'Mumbai, IN', markers: ['F500 Mid-Senior', 'Study Abroad'] },
    { name: 'Aditya Sharma', batch: 'M.Tech · 2017', company: 'Google', designation: 'Staff Engineer', location: 'Hyderabad, IN', markers: ['F500 Leadership', 'Media Mentions'] },
    { name: 'Vikram Rao', batch: 'M.Tech · 2016', company: 'Amazon', designation: 'Principal Engineer', location: 'Seattle, US', markers: ['F500 Leadership', '15+ yrs exp', 'Currently Abroad'] },
    { name: 'Arjun Nair', batch: 'B.Tech · 2014', company: 'GreenPass (Founder)', designation: 'Founder & CEO', location: 'Bangalore, IN', markers: ['Growing Start-up Founder', 'Media Mentions', 'Recruited in Past'] },
    { name: 'Priya Iyer', batch: 'B.Tech · 2018', company: 'Microsoft', designation: 'Senior SDE', location: 'Redmond, US', markers: ['F500 Mid-Senior', 'Currently Abroad', 'Sibling from same institute'] },
    { name: 'Neha Kapoor', batch: 'B.Des · 2019', company: 'Loomcraft Studio', designation: 'Founder', location: 'Mumbai, IN', markers: ['Growing Start-up Founder', 'Media Mentions'] },
    { name: 'Aditi Rao', batch: 'B.Tech · 2011', company: 'Salesforce', designation: 'Distinguished Engineer', location: 'San Francisco, US', markers: ['F500 Leadership', '15+ yrs exp', 'Currently Abroad'] },
];

export function initials(name: string) {
    return name
        .split(' ')
        .slice(0, 2)
        .map((s) => s[0])
        .join('')
        .toUpperCase();
}

export function reportById(id?: string) {
    return REPORTS.find((r) => r.id === id);
}
